import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil } from 'rxjs/operators';

import { User } from '../../models/User';
import { ConfirmationService } from '../../shared/components/confirmation-modal/confirmation.service';
import { IntegrationTemplateEventEditorModalComponent } from './event-editor-modal/event-editor-modal.component';
import { ToastService } from '../../shared/components/toast/toast.service';
import { Event as TemplateEvent } from '../../shared/models/event.model';
import {
  ApprovalStatus,
  IntegrationTemplate,
} from '../../shared/models/integration-template.model';
import { AuthService } from '../../shared/services/auth.service';
import {
  IntegrationEventPayload,
  IntegrationTemplateService,
} from '../../shared/services/integration-template.service';
import { IntegrationTemplateAdminService } from '../../shared/services/admin/integration-template-admin.service';
import { ADMINISTRATOR, UserService } from '../../shared/services/user.service';
import {
  IntegrationTemplateSuggestionService,
  PopularEvent,
  SuggestionItem,
} from '../../shared/services/integration-template-suggestion.service';

@Component({
  selector: 'app-integrations-templates',
  templateUrl: './integrations-templates.component.html',
  styleUrls: ['./integrations-templates.component.scss'],
})
export class IntegrationTemplatesComponent implements OnInit, OnDestroy {

  @ViewChild('modalEdit') modalTemplate: TemplateRef<unknown>;
  @ViewChild('modalSentForReview') modalSentForReviewTemplate: TemplateRef<unknown>;
  @ViewChild('modalRejectionReason') modalRejectionReasonTemplate: TemplateRef<unknown>;
  @ViewChild('modalClone') modalCloneTemplate: TemplateRef<unknown>;

  events: IntegrationTemplate[] = [];

  // Carregamento por bloco (CU-86akkubzz): a tela saiu do overlay global, então
  // cada requisição diz onde está — esqueleto na lista e no passo de eventos,
  // giro no botão que disparou a ação.
  loading = false;
  loadingDetail = false;
  saving = false;
  cloning = false;
  /** Linha com aprovação ou exclusão em andamento. */
  busyEventId: string | null = null;
  // F4: leadColumns removido (agora apenas em eventos individuais)

  rejectionReasonView: string | null = null;
  rejectionReasonEventName: string | null = null;
  postSaveEventForReview: IntegrationTemplate | null = null;
  reviewModalRef: NgbModalRef | null = null;

  currentPage = 1;
  perPage = 15;
  totalItems = 0;

  loggedUser: User | null = null;

  editingId: string | null = null;
  viewOnly = false;
  form: FormGroup;
  modalRef: NgbModalRef | null = null;

  // Duplicar plataforma (ação trazida da tela de Aprovações).
  cloneForm: FormGroup;
  cloneSourceId: string | null = null;
  cloneModalRef: NgbModalRef | null = null;
  currentStep = 1;
  readonly maxStep = 2;  // F4: Stepper 2 passos (Identificação + Eventos)

  // SMS-013: estado do Passo 2 — events com match_condition desta integração.
  templateEvents: TemplateEvent[] = [];
  /** Operation types (nichos) — usado no multiselect do Passo 1 e em sugestões SMS-006. */
  operationTypes: string[] = [];

  // Sugestões SMS-006
  suggestions: SuggestionItem[] = [];
  popularEvents: PopularEvent[] = [];
  private suggestionSubject = new Subject<{ name?: string; keys?: string[] }>();
  private destroy$ = new Subject<void>();

  constructor(
    private eventService: IntegrationTemplateService,
    private authService: AuthService,
    private modalService: NgbModal,
    private toastService: ToastService,
    private confirmation: ConfirmationService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private suggestionService: IntegrationTemplateSuggestionService,
    private userService: UserService,
    private adminService: IntegrationTemplateAdminService,
  ) {
    // SMS-013: Stepper 2 passos — Paso 1: Identificação (com operation_types) + Paso 2: Eventos
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(120)]],
      description: ['', [Validators.maxLength(500)]],
      operation_types: [[] as string[]],
    });

    this.cloneForm = this.fb.group({
      new_name: ['', [Validators.required, Validators.maxLength(120)]],
    });
  }

  ngOnInit(): void {
    this.loggedUser = this.authService.getUser();
    // F4: leadColumns removido do stepper principal (agora apenas em eventos)
    this.loadList();
    this.loadEventEditorDependencies();

    // Setup debounce para sugestões (SMS-006)
    this.suggestionSubject
      .pipe(
        debounceTime(500),
        switchMap((query) => this.suggestionService.suggest(query.name, query.keys)),
        takeUntil(this.destroy$)
      )
      .subscribe(
        (response) => {
          this.suggestions = response.data || [];
        },
        (error) => {
          this.suggestions = [];
        }
      );

    const editId = this.route.snapshot.queryParamMap.get('edit');
    if (editId) {
      this.eventService.findById(editId).subscribe((event) => {
        if (event) {
          this.openEdit(event);
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isAdmin(): boolean {
    return this.loggedUser?.profile_id === ADMINISTRATOR;
  }


  get modalTitle(): string {
    if (this.viewOnly) {
      return 'Visualizar Plataforma';
    }
    return this.editingId ? 'Editar Plataforma' : 'Nova Plataforma';
  }

  isOwn(event: IntegrationTemplate): boolean {
    if (!this.loggedUser) {
      return false;
    }
    return event.created_by_user_id === this.loggedUser.id;
  }

  isGlobal(event: IntegrationTemplate): boolean {
    return event.created_by_user_id === null;
  }

  canEdit(event: IntegrationTemplate): boolean {
    return this.isAdmin || this.isOwn(event);
  }

  loadList(): void {
    this.loading = true;
    this.eventService.listPaginated({ page: this.currentPage, perPage: this.perPage })
      .subscribe({
        next: (result) => {
          this.events = result.data;
          this.totalItems = result.total;
          this.perPage = result.per_page;
          this.currentPage = result.current_page;
          this.loading = false;
        },
        error: () => (this.loading = false),
      });
  }

  /**
   * SMS-013: carrega operation_types pro multiselect do Passo 1.
   * Defaults: array vazio em caso de erro.
   */
  private loadEventEditorDependencies(): void {
    this.userService.getOperationTypes()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (types: any[]) => {
          this.operationTypes = (types || [])
            .map((t: any) => (typeof t === 'string' ? t : t?.label))
            .filter((v: unknown): v is string => !!v);
        },
        error: () => {
          this.operationTypes = [];
        }
      });
  }

  /**
   * F5: abre o IntegrationTemplateEventEditorModalComponent para criar/editar eventos.
   * Quando o usuário salva no modal, acumulamos em `templateEvents`.
   * A persistência real acontece no submit do form pai (POST/PUT /api/integration-templates).
   */
  openEventEditor(event: TemplateEvent | null = null): void {
    const modalRef = this.modalService.open(IntegrationTemplateEventEditorModalComponent, { size: 'lg' });
    modalRef.componentInstance.event = event;

    modalRef.result.then(
      (result: TemplateEvent | true | undefined) => {
        if (!result || result === true) {
          return;
        }
        const saved = result as TemplateEvent;
        // Match por id existente (edição) ou append (criação).
        const existingIdx = saved.id
          ? this.templateEvents.findIndex(e => e.id === saved.id)
          : -1;
        if (existingIdx >= 0) {
          this.templateEvents[existingIdx] = saved;
        } else {
          this.templateEvents = [...this.templateEvents, saved];
        }
      },
      () => {},
    );
  }

  removeTemplateEvent(index: number): void {
    this.confirmation.show('Remover este evento da integração?', () => {
      this.templateEvents = this.templateEvents.filter((_, i) => i !== index);
    });
  }

  trackTemplateEvent(index: number, ev: TemplateEvent): string {
    return ev.id || `new-${index}`;
  }

  /**
   * SMS-013: resumo das condições. Aceita `match_condition` (novo) ou `predicate` (legacy).
   */
  matchConditionSummary(ev: TemplateEvent): string {
    const cond = (ev.match_condition && ev.match_condition.length > 0)
      ? ev.match_condition
      : (ev.predicate || []);
    if (cond.length === 0) {
      return 'Sem condições';
    }
    return cond
      .map((c: any) => {
        const op = c.operator;
        const noValue = op === 'exists' || op === 'existe';
        return `${c.field} ${op}${noValue ? '' : ' ' + (c.value ?? '')}`;
      })
      .join(' E ');
  }

  mappingCount(ev: TemplateEvent): number {
    return ev.mapping?.length || 0;
  }

  pageChanged(page: number): void {
    this.currentPage = page;
    this.loadList();
  }

  openCreate(): void {
    this.editingId = null;
    this.viewOnly = false;
    this.currentStep = 1;
    this.resetForm();
    // Reabilita o form: uma visualização anterior (viewOnly) deixa o form
    // desabilitado, e form.reset() não restaura o estado enabled/disabled.
    this.form.enable();
    this.suggestions = [];
    this.popularEvents = [];
    this.modalRef = this.modalService.open(this.modalTemplate, { size: 'lg', backdrop: 'static' });

    // Carrega popular events ao abrir modal (SMS-006)
    if (this.loggedUser?.operation_type) {
      this.suggestionService.popularEventsByNicho(this.loggedUser.operation_type)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (response) => {
            this.popularEvents = response.data || [];
          },
          () => {
            this.popularEvents = [];
          }
        );
    }
  }

  openEdit(event: IntegrationTemplate): void {
    // SMS-005: customer-não-admin editando evento approved → confirmação
    // ("voltará pra fila de aprovação"). Admin edita livre.
    const shouldConfirmReQueue =
      this.canEdit(event)
      && !this.isAdmin
      && event.approval_status === 'approved';

    if (shouldConfirmReQueue) {
      this.confirmation.show(
        'Editar este evento o enviará novamente para análise. Deseja continuar?',
        () => this.doOpenEdit(event),
      );
      return;
    }

    this.doOpenEdit(event);
  }

  private doOpenEdit(event: IntegrationTemplate): void {
    this.editingId = event.id;
    this.viewOnly = !this.canEdit(event);
    this.currentStep = 1;
    this.suggestions = [];
    this.popularEvents = [];
    this.modalRef = this.modalService.open(this.modalTemplate, { size: 'lg', backdrop: 'static' });

    // O item da lista não traz `events` (o index não faz eager-load). Busca o
    // template completo pra carregar os eventos do Passo 2; em erro, cai no item da lista.
    this.fillForm(event);
    this.loadingDetail = true;
    this.eventService.findById(event.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (full) => {
          this.fillForm(full ?? event);
          this.loadingDetail = false;
        },
        error: () => (this.loadingDetail = false),
      });

    // Carrega popular events ao abrir modal (SMS-006)
    if (this.loggedUser?.operation_type) {
      this.suggestionService.popularEventsByNicho(this.loggedUser.operation_type)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (response) => {
            this.popularEvents = response.data || [];
          },
          () => {
            this.popularEvents = [];
          }
        );
    }
  }

  view(event: IntegrationTemplate): void {
    this.doOpenEdit(event);
  }

  statusLabel(status: ApprovalStatus | undefined): string {
    switch (status) {
      case 'pending': return 'Em análise';
      case 'rejected': return 'Rejeitado';
      case 'approved': return 'Aprovado';
      default: return 'Aprovado';
    }
  }

  statusClass(status: ApprovalStatus | undefined): string {
    switch (status) {
      case 'pending': return 'bg-warning text-dark';
      case 'rejected': return 'bg-danger';
      case 'approved': return 'bg-success';
      default: return 'bg-success';
    }
  }

  isPending(event: IntegrationTemplate): boolean {
    return event.approval_status === 'pending';
  }

  isRejected(event: IntegrationTemplate): boolean {
    return event.approval_status === 'rejected';
  }

  openRejectionReason(event: IntegrationTemplate): void {
    if (!this.isRejected(event)) {
      return;
    }
    this.rejectionReasonView = event.rejection_reason ?? '';
    this.rejectionReasonEventName = event.name;
    this.modalService.open(this.modalRejectionReasonTemplate, { size: 'md' });
  }

  editFromRejection(event: IntegrationTemplate): void {
    // Reabre fluxo de edição normal (não usa confirm — já está rejected,
    // ao salvar volta pra pending via patch backend).
    this.doOpenEdit(event);
  }

  /** Aprova a plataforma (ação trazida da tela de Aprovações). */
  approve(event: IntegrationTemplate): void {
    this.confirmation.show(`Aprovar a plataforma "${event.name}"?`, () => {
      this.busyEventId = event.id;
      this.adminService.approve(event.id).subscribe({
        next: () => {
          this.busyEventId = null;
          this.toastService.success('Plataforma aprovada.');
          this.loadList();
          this.adminService.refreshPendingApprovals();
        },
        error: () => (this.busyEventId = null),
      });
    });
  }

  /** Abre o modal de duplicação da plataforma (ação trazida da tela de Aprovações). */
  openClone(event: IntegrationTemplate): void {
    this.cloneSourceId = event.id;
    this.cloneForm.reset({ new_name: `${event.name} (cópia)` });
    this.cloneModalRef = this.modalService.open(this.modalCloneTemplate, { size: 'md' });
  }

  doClone(): void {
    if (this.cloneForm.invalid || !this.cloneSourceId) {
      this.cloneForm.markAllAsTouched();
      return;
    }

    const newName = (this.cloneForm.value.new_name ?? '').trim();

    this.cloning = true;
    this.adminService.clone(this.cloneSourceId, newName).subscribe({
      next: () => {
        this.cloning = false;
        this.toastService.success('Plataforma duplicada com sucesso!');
        this.cloneModalRef?.close();
        this.cloneModalRef = null;
        this.cloneSourceId = null;
        this.loadList();
        // Clone do admin entra como approved (não muda pendentes), mas se a regra
        // mudar no backend basta o subject ser republicado pelo refresh.
        this.adminService.refreshPendingApprovals();
      },
      error: () => (this.cloning = false),
    });
  }

  delete(event: IntegrationTemplate): void {
    this.confirmation.show(`Deseja realmente excluir o evento "${event.name}"?`, () => {
      this.busyEventId = event.id;
      this.eventService.delete(event.id).subscribe({
        next: () => {
          this.busyEventId = null;
          this.toastService.success('Evento excluído com sucesso!');
          this.adminService.refreshPendingApprovals();
          this.loadList();
        },
        error: () => (this.busyEventId = null),
      });
    });
  }

  nextStep(): void {
    if (this.currentStep < this.maxStep) {
      this.currentStep += 1;
    }
  }

  prevStep(): void {
    if (this.currentStep > 1) {
      this.currentStep -= 1;
    }
  }

  save(): void {
    if (this.viewOnly) {
      this.modalRef?.close();
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toastService.info('Preencha os campos obrigatórios antes de salvar.');
      return;
    }

    const payload = this.buildPayload();
    const wasEditing = !!this.editingId;

    this.saving = true;
    this.eventService.save(payload, this.editingId ?? undefined).subscribe({
      next: (savedEvent) => {
        this.saving = false;
        this.toastService.success(
          wasEditing ? 'Evento atualizado com sucesso!' : 'Evento criado com sucesso!',
        );
        this.modalRef?.close();
        this.modalRef = null;
        this.loadList();
        // Atualiza badge do menu — só admin pode chamar o endpoint /admin/pending,
        // então não-admin não dispara aqui (a contagem refresca pro admin no
        // próximo load do menu).
        if (this.isAdmin) {
          this.adminService.refreshPendingApprovals();
        }

        // SMS-005: customer não-admin que acabou de salvar (criar OU editar)
        // recebe modal explicando que o evento entrou na fila de análise.
        if (!this.isAdmin && savedEvent?.approval_status === 'pending') {
          this.postSaveEventForReview = savedEvent;
          this.reviewModalRef = this.modalService.open(
            this.modalSentForReviewTemplate,
            { size: 'md', backdrop: 'static' },
          );
        }
      },
      error: () => (this.saving = false),
    });
  }

  closeSentForReviewModal(): void {
    this.reviewModalRef?.close();
    this.reviewModalRef = null;
    this.postSaveEventForReview = null;
  }

  trackById(_index: number, event: IntegrationTemplate): string {
    return event.id;
  }

  // F4: SMS-006 sugestões apenas por nome agora (payload é por evento individual)
  onNameChange(name: string): void {
    if (name && name.trim().length > 0) {
      this.suggestionSubject.next({ name: name.trim(), keys: undefined });
    } else {
      this.suggestions = [];
    }
  }

  /** Nomes dos templates sugeridos, prontos para interpolação inline no banner SMS-006. */
  get suggestionNames(): string {
    return this.suggestions.map((s) => s.name).join(', ');
  }

  useSuggestion(suggestion: SuggestionItem): void {
    // Fecha o modal e vai para a tela de integrações, que abre o form de
    // "Nova integração" e pré-seleciona a plataforma a partir de ?template_id.
    this.modalRef?.close();
    this.router.navigate(['/integrations'], { queryParams: { template_id: suggestion.id } });
  }

  // F4: Métodos de payload parsing removidos (agora em EventEditorModalComponent)

  private resetForm(): void {
    this.form.reset({
      name: '',
      description: '',
      operation_types: [],
    });
    this.templateEvents = [];
  }

  private fillForm(event: IntegrationTemplate): void {
    this.form.patchValue({
      name: event.name,
      description: event.description ?? '',
      operation_types: event.operation_types || [],
    });

    // SMS-011c: events vêm eager-loaded do GET /api/integration-templates/{id}.
    this.templateEvents = (event.events || []).map(e => ({ ...e }));

    if (this.viewOnly) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  private buildPayload(): IntegrationEventPayload {
    const raw = this.form.getRawValue();

    return {
      name: (raw.name ?? '').trim(),
      description: raw.description?.trim() ? raw.description.trim() : undefined,
      operation_types: raw.operation_types || [],
      // SMS-013: events com match_condition + mapping + lead_list_id vão na mesma transação.
      events: this.templateEvents.length > 0
        ? this.templateEvents.map((e, i) => ({ ...e, sort_order: e.sort_order ?? i }))
        : undefined,
    };
  }
}
